import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Item from "./Item";
import { fixedDigitsAfterDecimal } from "../../../../utils";
import PieChartComp from "./PieChart";
import { useResponsive } from "../../../../hooks/useResponsive";
import { ByBoardHeader } from "./ByBoardHeader";
import { BoardAvatar } from "./BoardAvatar";

function ByBoardTab({ worklogs }) {
  const activeBoard = useSelector((state) => state.activeBoard.activeBoardList);
  const checkedList = useSelector((state) => state.search.boardSearch);
  const [expandedPanel, setExpandedPanel] = useState([]);
  const totalHours = useSelector((state) => state.search.totalWorkHours);
  const searchText = useSelector((state) => state.search.searchText);
  const isMobile = useResponsive("down", "md");

  const handleExpandAccordianChange = useCallback(
    (boardIndex) => (event, isExpanded) => {
      const boardData = expandedPanel.map((element) => {
        if (element?.board_id === boardIndex) {
          return { ...element, isExpand: isExpanded };
        } else {
          return { ...element };
        }
      });
      setExpandedPanel(boardData);
    },
    [expandedPanel]
  );

  const selectedBoard = useMemo(() => {
    return checkedList.length === 0
      ? activeBoard
      : checkedList.map((boardId) =>
          activeBoard.find((board) => board.board_id === boardId)
        );
  }, [checkedList, activeBoard]);

  const handleExpandAll = useCallback(
    (event) => {
      event.stopPropagation();
      const expandData = activeBoard.map((element) => ({
        board_id: element.board_id,
        isExpand: event.target.checked,
      }));
      setExpandedPanel(expandData);
    },
    [activeBoard]
  );

  useEffect(() => {
    if (activeBoard) {
      const expandData = activeBoard.map((element, index) => {
        return { board_id: element.board_id, isExpand: false };
      });
      setExpandedPanel(expandData);
    }
  }, []);

  const filteredWorklogs = useMemo(() => {
    return worklogs.filter((cardData) => {
      return (
        cardData?.key?.toLowerCase().includes(searchText) ||
        cardData?.cardlog?.card?.card_id.toString().includes(searchText) ||
        cardData?.cardlog?.user?.first_name
          ?.toLowerCase()
          .includes(searchText) ||
        cardData?.cardlog?.user?.last_name
          ?.toLowerCase()
          .includes(searchText) ||
        cardData?.cardlog?.card?.title.toString().includes(searchText) ||
        cardData?.cardlog?.work_performed?.toString().includes(searchText)
      );
    });
  }, [worklogs, searchText]);

  return (
    <>
      {selectedBoard.length > 0 && (
        <div className="d-flex justify-content-center flex-dir-col">
          <PieChartComp
            data={selectedBoard
              .map((boardData) => {
                return {
                  name: boardData.board_name,
                  value: fixedDigitsAfterDecimal(
                    worklogs
                      .filter(
                        (worklog) =>
                          worklog?.cardlog?.card?.board_id ===
                          boardData.board_id
                      )
                      .reduce((sum, item) => sum + item?.cardlog.hours_spent, 0)
                  ),
                };
              })
              .filter((ele) => ele.value !== 0)}
            totalHours={totalHours}
          />
        </div>
      )}
      <div className="worklog_board_cont">
        <ByBoardHeader
          handleExpandAll={handleExpandAll}
          checked={expandedPanel.every((element) => element.isExpand)}
        />
        {selectedBoard.length > 0 ? (
          selectedBoard
            .filter((boardDetails, index) => {
              return (
                worklogs
                  .filter(
                    (worklog) =>
                      worklog?.cardlog?.card?.board_id === boardDetails.board_id
                  )
                  .reduce((sum, item) => sum + item?.cardlog.hours_spent, 0) !==
                0
              );
            })
            .map((boardData, idx) => {
              return (
                <Accordion
                  key={`worklog-by-board-${idx}`}
                  expanded={
                    expandedPanel?.find(
                      (board) => board?.board_id === boardData.board_id
                    )?.isExpand || false
                  }
                  className="accordion-allocation-board"
                  onChange={handleExpandAccordianChange(boardData.board_id)}>
                  <AccordionSummary
                    sx={{ marginRight: isMobile ? "0px" : "30px" }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header">
                    <div className="worklog_board_accordian_header">
                      <BoardAvatar boardData={boardData} />
                      <p className="total_project_hours">
                        {fixedDigitsAfterDecimal(
                          worklogs
                            .filter(
                              (worklog) =>
                                worklog?.cardlog?.card?.board_id ===
                                boardData.board_id
                            )
                            .reduce(
                              (sum, item) => sum + item?.cardlog.hours_spent,
                              0
                            )
                        )}
                      </p>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="pl-38">
                    <Accordion sx={{ boxShadow: "none" }}>
                      <AccordionDetails>
                        {filteredWorklogs.filter(
                          (worklog) =>
                            worklog?.cardlog?.card?.board_id ===
                            boardData.board_id
                        ).length > 0 ? (
                          filteredWorklogs
                            .filter(
                              (worklog) =>
                                worklog?.cardlog?.card?.board_id ===
                                boardData.board_id
                            )
                            .map((worklog, idx) => (
                              <Item
                                worklog={worklog}
                                key={`filter-worklog-by-board-${idx}`}
                              />
                            ))
                        ) : (
                          <p className="align_text_center">No worklog found</p>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </AccordionDetails>
                </Accordion>
              );
            })
        ) : (
          <p className="loader-center">No Board Selected</p>
        )}
      </div>
    </>
  );
}

export default ByBoardTab;
